import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Toast,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS,
} from 'cdk-radial';
import './style.css';
import { APIDetails } from '@cdk-prod/api-directory-component';
import {
  setAPIDirectoryPage,
  sendEmailToConsumer,
  sendEmailToProvider,
  getAPIDetailsById,
  getConsentTerms,
  getDomains,
  getAPIDetailsByIdInBundle,
  getAPISpecByIdInBundle
} from '../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';
import {
  API_DIRECTORY_PAGE,
  API_DIRECTORY_THEME,
  API_DETAILS_HANDLERS_MODEL,
  EMAIL_MESSAGE,
  ADD_API_To_CART_FAILURE_MESSAGE,
} from '../../../constants/APIDirectoryConstants';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../utils/amplitude';
import {
  addAPIToCart,
  submitRestrictedAccessRequest,
} from '../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES,
} from '../../../constants/amplitudeConstants';
import { environmentURLs } from '../../common/CaptureEnvironment';
import { updateAPIPricingPlanId } from '../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { BUNDLE_TYPE, MSA_CONSENT_STATUS } from '../../../constants/constants';
import { getSinglePlanId, mapCartForDirectory } from '../utils/apiPricingUtil';
import {
  trackPageView,
  PAGE_SECTIONS,
  PAGE_SUBSECTIONS,
  trackApiAddedToCart,
  trackBundleAddedToCart,
  trackApiInformationRequested,
  trackSelectApiPlan,
  trackSelectBundlePlan,
  trackComparePrice,
  trackSelectApiVersion,
  trackAdjustedCalls,
} from '../../../utils/gtm';
const MY_APIS_URL = 'my-apis';
const API_DIR_URL = 'api-directory';
const apiDirTheme = API_DIRECTORY_THEME.DEALER_API_DIRECTORY;

export const handleApiRedirection = (payload, token, dispatch) => {
  const { apiId, apiType, bundleDetails, prevScreen } = payload || {};
  dispatch(getAPIDetailsById({ apiId, apiType, token }));
  dispatch(
    setAPIDirectoryPage({
      page: API_DIRECTORY_PAGE.DETAILS,
      props: { apiId, bundleDetails, prevScreen },
    }),
  );
};
export const fetchApiDetails = (payload, token, dispatch) => {
  const { apiId, apiType, bundleId } = payload || {};
  return dispatch(
    getAPIDetailsByIdInBundle({
      apiId,
      apiType,
      bundleId,
      token,
    }),
  ).then((res) => {
    if (res.error) throw res.error;
  });
};

export const fetchApiSpec = (payload, token, dispatch) => {
  const { apiId, apiType, bundleId } = payload || {};
  return dispatch(
    getAPISpecByIdInBundle({
      apiId,
      apiType,
      bundleId,
      token,
    }),
  ).then((res) => {
    if (res.error) throw res.error;
  });
};

export const handleAddBundleIntoCart = (
  payload,
  token,
  dispatch,
  orgAmplitudeProperties,
  bundleData,
  cart = [],
) => {
  sendAmplitudeData(
    AMPLITUDE_EVENTS.BUNDLE_DETAILS_ADD_TO_CART,
    orgAmplitudeProperties,
  );

  // Auto-add pricing plan if bundle has only one plan
  const singlePlanId = getSinglePlanId(bundleData?.plans);
  const plan = singlePlanId ? bundleData?.plans?.find(p => p.id === singlePlanId) : null;
  const hasPlans = bundleData?.plans && bundleData.plans.length > 0;
  const apisCount = (!hasPlans && bundleData?.apis) ? bundleData.apis.length : 1;
  trackBundleAddedToCart(bundleData, plan, cart.length + apisCount);

  let updatedPayload = { ...payload };
  if (!hasPlans && payload.cart && Array.isArray(payload.cart)) {
    const apiMap = new Map(bundleData?.apis?.map((api) => [api.id, api]));
    updatedPayload.cart = payload.cart.map((cartItem) => {
      const matchingApi = apiMap.get(cartItem.resourceId);
      if (matchingApi) {
        const apiSinglePlanId = getSinglePlanId(matchingApi.plans);
        if (apiSinglePlanId) {
          return {
            ...cartItem,
            pricingPlanId: apiSinglePlanId,
          };
        }
      }
      return cartItem;
    });
  } else if (singlePlanId) {
    updatedPayload = { ...payload, bundlePricingPlanId: singlePlanId };
  }

  dispatch(addAPIToCart({ payload: updatedPayload, token }));
};

const updatePricingPlanOfAPI = (payload, dispatch, token) => {
  sendAmplitudeData(AMPLITUDE_EVENTS.UPDATE_PRICING_PLAN_OF_API_IN_CART, {
    [AMPLITUDE_PROPERTIES.APIS]: {
      [AMPLITUDE_PROPERTIES.RESOURCE_NAME]: payload?.Name || '',
      [AMPLITUDE_PROPERTIES.RESOURCE_ID]: payload?.resourceId || '',
      [AMPLITUDE_PROPERTIES.RESOURCE_TYPE]: payload?.resourceType || '',
    },
  });

  const data = {
    resourceId: payload?.resourceId,
    resourcePricingPlanId: payload?.planId || '',
  };
  payload = { ...data };
  dispatch(updateAPIPricingPlanId({ payload, token }));
};

export const selectPricingPlan = (
  payload,
  token,
  dispatch,
  cartRef,
  apiInfoRef,
  isBundle,
  orgAmplitudeProperties,
  bundleDataRef,
) => {
  const cart = cartRef?.current || cartRef;
  const apiInfo = apiInfoRef?.current || apiInfoRef;
  const bundleData = bundleDataRef?.current || bundleDataRef;

  if (isBundle)
    sendAmplitudeData(
      AMPLITUDE_EVENTS.BUNDLE_DETAILS_PRICING_PLAN_ADD_TO_CART,
      orgAmplitudeProperties,
    );
  else
    sendAmplitudeData(
      AMPLITUDE_EVENTS.API_DETAILS_PRICING_PLAN_ADD_TO_CART,
      orgAmplitudeProperties,
    );
  const apiCartItem =
    Array.isArray(cart) &&
    cart.find((api) => api.resourceId === payload?.resourceId);
  if (apiCartItem) {
    updatePricingPlanOfAPI(payload, dispatch, token);
    trackSelectApiPlan(payload?.planName, payload?.resourceId, payload?.Name, payload?.resourceType);
  } else {
    sendAmplitudeData(AMPLITUDE_EVENTS.ADD_API_TO_CART_WITH_PRICING, {
      [AMPLITUDE_PROPERTIES.APIS]: {
        [AMPLITUDE_PROPERTIES.RESOURCE_NAME]: payload?.Name || '',
        [AMPLITUDE_PROPERTIES.RESOURCE_ID]: payload?.resourceId || '',
        [AMPLITUDE_PROPERTIES.RESOURCE_TYPE]: payload?.resourceType || '',
      },
    });

    // Resolve api name and plan details — fall back to bundleData when in bundle context
    let apiName = payload?.Name;
    let planName = payload?.planName;
    let planPrice = payload?.price;

    if (isBundle && bundleData) {
      const bundleApi = bundleData?.apis?.find((api) => api.id === payload?.resourceId);
      if (bundleApi) {
        apiName = bundleApi.name || apiName;
        const selectedPlan = bundleApi.plans?.find((p) => p.id === payload?.planId);
        if (selectedPlan) {
          planName = selectedPlan.name || planName;
          planPrice = selectedPlan.price || planPrice;
        }
      }
    }

    const apiData = {
      id: payload?.resourceId,
      name: apiName,
      kind: payload?.resourceType || apiInfo?.data?.kind
    };
    const plan = {
      name: planName,
      price: planPrice
    };
    trackApiAddedToCart(apiData, plan, (cart?.length || 0) + 1);

    const data = {
      cart: [
        {
          resourceId: payload?.resourceId || '',
          resourceType: payload?.resourceType || apiInfo?.data?.kind,
          pricingPlanId: payload?.planId,
        },
      ],
    };
    payload = { ...data };
    dispatch(addAPIToCart({ payload, token }));
  }
};

export const selectBundlePricingPlan = (
  payload,
  token,
  dispatch,
  cartRef,
  orgAmplitudeProperties,
  bundleDataRef,
) => {
  const cart = cartRef?.current || cartRef;
  const bundleData = bundleDataRef?.current || bundleDataRef;

  sendAmplitudeData(
    AMPLITUDE_EVENTS.BUNDLE_DETAILS_PRICING_PLAN_ADD_TO_CART,
    orgAmplitudeProperties,
  );

  // Resolve plan details from bundleData using planId
  const selectedPlan = bundleData?.plans?.find((plan) => plan?.id === payload?.planId);
  const planName = selectedPlan?.name || payload?.planName || payload?.planId;
  const planPrice = selectedPlan?.price || selectedPlan?.subscriptionPrice || payload?.price || 0;
  const bundleName = bundleData?.name || payload?.Name || '';

  const isBundleInCart =
    Array.isArray(cart) &&
    cart.find((api) => api.resourceId === payload?.resourceId);
  if (isBundleInCart) {
    updatePricingPlanOfAPI(payload, dispatch, token);
    trackSelectBundlePlan(planName, payload?.resourceId, bundleName);
  } else {
    sendAmplitudeData(AMPLITUDE_EVENTS.ADD_API_TO_CART_WITH_PRICING, {
      [AMPLITUDE_PROPERTIES.APIS]: {
        [AMPLITUDE_PROPERTIES.RESOURCE_NAME]: bundleName,
        [AMPLITUDE_PROPERTIES.RESOURCE_ID]: payload?.resourceId || '',
        [AMPLITUDE_PROPERTIES.RESOURCE_TYPE]: payload?.resourceType || '',
      },
    });

    const bundleDataForCart = {
      id: payload?.resourceId || payload?.apiId,
      name: bundleName,
      apis: payload?.bundleApis || []
    };
    const plan = {
      name: planName,
      price: planPrice
    };
    trackBundleAddedToCart(bundleDataForCart, plan, (cart?.length || 0) + 1);

    const data = {
      bundleId: payload?.resourceId || payload?.apiId,
      bundleType: BUNDLE_TYPE.PRICE,
      bundlePricingPlanId: payload?.planId || '',
      cart: (payload?.bundleApis || []).map((api) => ({
        resourceId: api.id,
        resourceType: api.type,
      })),
    };
    dispatch(addAPIToCart({ payload: { ...data }, token }));
  }
};

const APIDetailsContainer = ({ apiId, handleMyCart, location }) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const {
    isAuthenticated,
    decodedToken: { firstName, lastName } = {},
    userEmail,
    authenticationToken: token,
    isDealerPurchaseDisabled,
    hasDAABAccess,
    fortellisEnterpriseEntityId,
    entityDetails,
  } = state?.loginDetailsSlice;
  const {
    apiInfo,
    fetchingAPIDetails,
    errorInFetchingAPIDetails,
    fetchingBundleDetails,
    errorInFetchingBundleDetails,
    domains,
    consumerEmailMessage,
    providerEmailMessage,
    bundleData,
    bundleDetailsViewEnabled,
    daabConsentDetails,
    fetchingAPIDetailsInBundle,
    errorInFetchingAPIDetailsInBundle,
    bundles,
    fetchingAPISpecInBundle,
    errorInFetchingAPISpecInBundle,
  } = state?.apiDirectorySlice;
  const {
    cart,
    orders,
    addingApiToCart,
    errorAddingApiToCart,
    addingPricingPlanToApiInCart,
    errorAddingPricingPlanToApiInCart,
  } = state?.orderAPISlice;
  const [showToast, setShowToast] = useState(false);
  const [toastVriant, setToastVariant] = useState(TOAST_VARIANTS.POSITIVE);
  const [toastContent, setToastContent] = useState('');
  const [loadingRestrictedAccess, setLoadingRestrictedAccess] = useState(false);

  const bundleDataRef = React.useRef(bundleData);
  bundleDataRef.current = bundleData;
  const cartRef = React.useRef(cart);
  cartRef.current = cart;
  const apiInfoRef = React.useRef(apiInfo);
  apiInfoRef.current = apiInfo;

  const mappedCart = React.useMemo(() => mapCartForDirectory(cart), [cart]);

  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice,
  );
  const domainsProp = domains?.items?.[1]?.dimensions || [];
  const isMsaConsented =
    daabConsentDetails?.consentStatus === MSA_CONSENT_STATUS.CONSENTED;
  const authProp = {
    isAuthenticated,
    userData: {
      name: `${firstName || ''} ${lastName || ''}`,
      email: userEmail,
    },
    orgData: {
      id: fortellisEnterpriseEntityId,
      name: entityDetails?.organizationName || '',
    },
  };


  useEffect(() => {
    const subsection = bundleDetailsViewEnabled ? PAGE_SUBSECTIONS.BUNDLE_DETAILS : PAGE_SUBSECTIONS.API_DETAILS;
    // Only track pageview when API/bundle data is loaded
    if (bundleDetailsViewEnabled && bundleData?.id) {
      trackPageView({ page_section: PAGE_SECTIONS.API_DIRECTORY, page_subsection: subsection });
    } else if (!bundleDetailsViewEnabled && apiInfo?.data?.id) {
      trackPageView({ page_section: PAGE_SECTIONS.API_DIRECTORY, page_subsection: subsection });
    }
  }, [bundleDetailsViewEnabled, apiId, apiInfo?.data?.id, bundleData?.id]);
  const envURLs = { DOCS_UI_URL: environmentURLs.REACT_APP_NAV_DOCS_URL };
  useEffect(() => {
    if (
      daabConsentDetails &&
      !hasDAABAccess &&
      Object.keys(daabConsentDetails).length <= 0
    ) {
      dispatch(getConsentTerms({ token }));
    }
    if (domains && Object.keys(domains).length <= 0) {
      dispatch(getDomains({ token }));
    }
  }, []);

  useEffect(() => {
    if (
      consumerEmailMessage === EMAIL_MESSAGE.SUCCESS &&
      providerEmailMessage === EMAIL_MESSAGE.SUCCESS
    ) {
      setToastVariant(TOAST_VARIANTS.POSITIVE);
      setShowToast(true);
    } else if (
      consumerEmailMessage === EMAIL_MESSAGE.FAILURE ||
      providerEmailMessage === EMAIL_MESSAGE.FAILURE
    ) {
      setToastVariant(TOAST_VARIANTS.NEGATIVE);
      setShowToast(true);
    }
  }, [consumerEmailMessage, providerEmailMessage]);

  useEffect(() => {
    if (
      errorAddingApiToCart === true ||
      errorAddingPricingPlanToApiInCart === true
    ) {
      setShowToast(true);
      setToastVariant(TOAST_VARIANTS.NEGATIVE);
      setToastContent(ADD_API_To_CART_FAILURE_MESSAGE);
    }
  }, [errorAddingApiToCart, errorAddingPricingPlanToApiInCart]);

  const onFilterClick = (props) => {
    dispatch(
      setAPIDirectoryPage({ page: API_DIRECTORY_PAGE.HOME, props: props }),
    );
  };
  const contactConsumer = (payload) => {
    dispatch(sendEmailToConsumer({ payload, token }));
  };
  const contactProvider = (payload) => {
    dispatch(sendEmailToProvider({ payload, token }));
  };

  const addToCart = (payload) => {
    sendAmplitudeData(AMPLITUDE_EVENTS.ADD_TO_CART, {
      [AMPLITUDE_PROPERTIES.APIS]: {
        [AMPLITUDE_PROPERTIES.RESOURCE_NAME]: payload?.Name || '',
        [AMPLITUDE_PROPERTIES.RESOURCE_ID]: payload?.resourceId || '',
        [AMPLITUDE_PROPERTIES.RESOURCE_TYPE]: payload?.resourceType || '',
      },
    });

    // Handle regular API with single plan auto-select
    const pricingPlanId = getSinglePlanId(apiInfo?.data?.plans);
    const plan = pricingPlanId ? apiInfo?.data?.plans?.find(p => p.id === pricingPlanId) : null;
    trackApiAddedToCart(apiInfo?.data, plan, cart.length + 1);

    const cartPayload = {
      cart: [
        {
          resourceId: payload?.resourceId,
          resourceType: payload?.resourceType,
          ...(pricingPlanId && { pricingPlanId }),
        },
      ],
    };

    dispatch(addAPIToCart({ payload: cartPayload, token }));
  };

  const requestRestrictedAccess = (payload) => {
    const { apiId, apiType } = payload || {};
    trackApiInformationRequested(apiInfo?.data, cart.length);
    setLoadingRestrictedAccess(true);
    dispatch(submitRestrictedAccessRequest({ apiId, apiType, token }))
      .unwrap()
      .then(() => {
        setToastVariant(TOAST_VARIANTS.POSITIVE);
        setToastContent(
          'Your request has been submitted and you will be updated via email.',
        );
        setShowToast(true);
      })
      .finally(() => {
        setLoadingRestrictedAccess(false);
      });
  };

  const redirectToMyAPIs = () => {
    let url = window.location.href.split(`/${API_DIR_URL}`)[0];
    localStorage.setItem('dplocationWithoutApp', `${url}/${MY_APIS_URL}`);
    window.location.assign(`${url}/${MY_APIS_URL}`);
  };

  const handlers = {
    ...API_DETAILS_HANDLERS_MODEL,
    apiTypeFilter: onFilterClick,
    apiCategoryFilter: onFilterClick,
    sendEmailToConsumer: contactConsumer,
    sendEmailToProvider: contactProvider,
    addToCart: addToCart,
    openMyCart: handleMyCart,
    viewInMyAPIs: redirectToMyAPIs,
    selectPricingPlan: (payload) =>
      selectPricingPlan(
        payload,
        token,
        dispatch,
        cartRef,
        apiInfoRef,
        bundleDetailsViewEnabled,
        orgAmplitudeProperties,
        bundleDataRef,
      ),
    selectBundlePricingPlan: (payload) =>
      selectBundlePricingPlan(
        payload,
        token,
        dispatch,
        cartRef,
        orgAmplitudeProperties,
        bundleDataRef,
      ),
    requestRestrictedAccess: requestRestrictedAccess,
    trackComparePrice: trackComparePrice,
    trackSelectApiVersion: trackSelectApiVersion,
    trackAdjustedCalls: trackAdjustedCalls,
  };

  const getApiInfoForCurrentView = () => {
    if (bundleDetailsViewEnabled) {
      return null;
    }
    if (apiInfo?.data?.id === apiId) {
      return apiInfo;
    }
    return null;
  };

  const homePageClickHandlers = {
    handleApiRedirection: (payload) =>
      handleApiRedirection(payload, token, dispatch),
    fetchApiDetails: (payload) => fetchApiDetails(payload, token, dispatch),
    fetchApiSpec: (payload) => fetchApiSpec(payload, token, dispatch),
    addBundleIntoCart: (payload) =>
      handleAddBundleIntoCart(
        payload,
        token,
        dispatch,
        orgAmplitudeProperties,
        bundleData,
        cart,
      ),
    viewInMyAPIs: redirectToMyAPIs,
  };

  return (
    <>
      <APIDetails
        location={location}
        apiId={apiId}
        apiInfo={getApiInfoForCurrentView()}
        fetchingAPIDetails={fetchingAPIDetails}
        fetchingAPIDetailsInBundle={fetchingAPIDetailsInBundle}
        fetchingBundleDetails={fetchingBundleDetails}
        fetchingAPISpecInBundle={fetchingAPISpecInBundle}
        errorInFetchingAPISpecInBundle={errorInFetchingAPISpecInBundle}
        errorInFetchingBundleDetails={errorInFetchingBundleDetails}
        errorInFetchingAPIDetails={errorInFetchingAPIDetails}
        errorInFetchingAPIDetailsInBundle={errorInFetchingAPIDetailsInBundle}
        apiDetailsOnClickHandlers={handlers}
        cartDetails={mappedCart}
        orderDetails={orders}
        domainDisplayNames={domainsProp}
        auth={authProp}
        belongsTo={apiDirTheme}
        sendAmplitudeData={sendAmplitudeData}
        environmentURLs={envURLs}
        isContractUIEnabled={true}
        apiHomeOnClickHandlers={homePageClickHandlers}
        bundleDetailsViewEnabled={bundleDetailsViewEnabled}
        bundleDetails={bundleData}
        addingPricingPlanToApiInCart={addingPricingPlanToApiInCart}
        addingAPIToCart={addingApiToCart}
        isMsaConsented={isMsaConsented || hasDAABAccess}
        loadingRestrictedAccess={loadingRestrictedAccess}
        isDealerPurchaseDisabled={isDealerPurchaseDisabled}
        bundles={bundles}
      />
      {showToast && (
        <Toast
          variant={toastVriant}
          content={providerEmailMessage || toastContent}
          id={'contact-provider-email-toast'}
          actions={[
            {
              onClick: () => {
                setShowToast(false);
              },
              text: 'Dismiss',
            },
          ]}
          onVisibilityDurationEnd={() => {
            setShowToast(false);
          }}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}
    </>
  );
};
export default APIDetailsContainer;
